import { useCallback } from 'react';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Capacitor } from '@capacitor/core';

export interface NativeGestureOptions {
  onSwipeDown?: () => void;
  onSwipeUp?: () => void;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onPullDown?: () => void;
  hapticFeedback?: boolean;
  threshold?: number;
}

export const useNativeGestures = (options: NativeGestureOptions = {}) => {
  const {
    onSwipeDown,
    onSwipeUp,
    onSwipeLeft,
    onSwipeRight,
    onPullDown,
    hapticFeedback = true,
    threshold = 50
  } = options;

  // Haptic feedback functions
  const triggerLight = useCallback(async () => {
    if (hapticFeedback && Capacitor.isNativePlatform()) {
      try {
        await Haptics.impact({ style: ImpactStyle.Light });
      } catch (error) {
        console.log('Haptic feedback not available');
      }
    }
  }, [hapticFeedback]);

  const triggerMedium = useCallback(async () => {
    if (hapticFeedback && Capacitor.isNativePlatform()) {
      try {
        await Haptics.impact({ style: ImpactStyle.Medium });
      } catch (error) {
        console.log('Haptic feedback not available');
      }
    }
  }, [hapticFeedback]);

  const triggerHeavy = useCallback(async () => {
    if (hapticFeedback && Capacitor.isNativePlatform()) {
      try {
        await Haptics.impact({ style: ImpactStyle.Heavy });
      } catch (error) {
        console.log('Haptic feedback not available');
      }
    }
  }, [hapticFeedback]);

  // Enhanced gesture handlers with haptic feedback
  const handleSwipeStart = useCallback(() => {
    triggerLight();
  }, [triggerLight]);

  const handleSwipeEnd = useCallback((direction: string) => {
    triggerMedium();
    
    switch (direction) {
      case 'down':
        onSwipeDown?.();
        break;
      case 'up':
        onSwipeUp?.();
        break;
      case 'left':
        onSwipeLeft?.();
        break;
      case 'right':
        onSwipeRight?.();
        break;
    }
  }, [onSwipeDown, onSwipeUp, onSwipeLeft, onSwipeRight, triggerMedium]);

  const handlePullDown = useCallback(() => {
    triggerHeavy();
    onPullDown?.();
  }, [onPullDown, triggerHeavy]);

  // Native-style momentum calculation
  const calculateMomentum = useCallback((velocity: number, distance: number) => {
    const deceleration = 0.0006;
    const momentum = Math.abs(velocity) / deceleration;
    return Math.min(momentum, Math.abs(distance) * 4);
  }, []);

  return {
    triggerLight,
    triggerMedium,
    triggerHeavy,
    handleSwipeStart,
    handleSwipeEnd,
    handlePullDown,
    calculateMomentum,
    isNativePlatform: Capacitor.isNativePlatform()
  };
};